import { useState } from "react"
import { Globe2, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@f2e/ui/components/base"

import { resolveLocalizedText } from "../registry"
import type { AgentTemplateWebSearchFieldDefinition } from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

type WebSearchFormValue = {
  enabled: boolean
  configuration: {
    scope?: "entire_web" | "specific_websites"
    manualDomains?: string[]
    boundary?: string
  }
}

export const normalizeWebSearchDomains = (value: string) => {
  const domains: string[] = []
  for (const rawDomain of value.split(",")) {
    const candidate = rawDomain.trim()
    if (!candidate) continue
    const hasHttpScheme =
      candidate.startsWith("http://") || candidate.startsWith("https://")
    if (!hasHttpScheme && candidate.includes(" ")) return undefined

    // Match validate_web_search_domain(), which treats the authority before the
    // first path, query, or fragment delimiter as urlparse().netloc.
    const valueAfterScheme = hasHttpScheme
      ? candidate.slice(candidate.indexOf("://") + 3)
      : candidate
    if (!valueAfterScheme.split(/[/?#]/, 1)[0]) return undefined
    if (!domains.includes(candidate)) domains.push(candidate)
  }
  return domains.length > 0 ? domains : undefined
}

const isWebSearchFormValue = (value: unknown): value is WebSearchFormValue =>
  typeof value === "object" &&
  value !== null &&
  "enabled" in value &&
  typeof value.enabled === "boolean" &&
  "configuration" in value &&
  typeof value.configuration === "object" &&
  value.configuration !== null

export const WebSearchItemRenderer = ({
  item,
  locale,
  value,
  values,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  const [domainInput, setDomainInput] = useState("")
  const [domainInputError, setDomainInputError] = useState(false)
  if (item.type !== "tool_web_search") return null
  const definition = item as AgentTemplateWebSearchFieldDefinition
  const toolValue: WebSearchFormValue = isWebSearchFormValue(value)
    ? value
    : {
        enabled: definition.required || definition.enabledByDefault === true,
        configuration: {},
      }
  const configuration = toolValue.configuration
  const scope = configuration.scope || definition.defaultScope
  const manualDomains = configuration.manualDomains || []
  const boundary = configuration.boundary || ""
  const derivedDomains = definition.domainAnswerKeys.reduce<string[]>(
    (domains, answerKey) => {
      const answer = values[answerKey]
      const normalizedDomains =
        typeof answer === "string"
          ? normalizeWebSearchDomains(answer)
          : undefined
      normalizedDomains?.forEach((domain) => {
        if (!domains.includes(domain)) domains.push(domain)
      })
      return domains
    },
    []
  )

  const updateConfiguration = (
    patch: Partial<WebSearchFormValue["configuration"]>
  ) => {
    onChange({
      enabled: toolValue.enabled,
      configuration: { ...configuration, ...patch },
    })
  }

  const addDomain = () => {
    const normalizedDomains = normalizeWebSearchDomains(domainInput)
    if (!normalizedDomains) {
      setDomainInputError(true)
      return
    }
    const allDomains = new Set([...derivedDomains, ...manualDomains])
    const newDomains = normalizedDomains.filter(
      (domain) => !allDomains.has(domain)
    )
    if (allDomains.size + newDomains.length > definition.maxDomains) {
      setDomainInputError(true)
      return
    }
    if (newDomains.length > 0) {
      updateConfiguration({ manualDomains: [...manualDomains, ...newDomains] })
    }
    setDomainInput("")
    setDomainInputError(false)
  }

  const domainCount = new Set([...derivedDomains, ...manualDomains]).size
  const summary =
    scope === "entire_web"
      ? t("workspace.agent.templates.webSearch.entireWebSummary")
      : t("workspace.agent.templates.webSearch.websiteSummary", {
          count: domainCount,
        })

  return (
    <ConfigurableFieldShell
      id={`template-${definition.key}`}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={toolValue.enabled}
      required={definition.required}
      disabled={disabled}
      summary={
        <span className="inline-flex items-center gap-1.5">
          <Globe2 className="size-3.5" aria-hidden="true" />
          {summary}
        </span>
      }
      error={error}
      onEnabledChange={(enabled) =>
        onChange({ enabled, configuration: toolValue.configuration })
      }
    >
      <div className="space-y-4 border-t border-border pt-4">
        {definition.scopeMode === "user_selectable" && (
          <div className="space-y-2">
            <Label htmlFor={`template-${definition.key}-scope`}>
              {t("workspace.agent.templates.webSearch.scope")}
            </Label>
            <Select
              value={scope}
              disabled={disabled}
              onValueChange={(nextScope) => {
                if (
                  nextScope === "entire_web" ||
                  nextScope === "specific_websites"
                ) {
                  updateConfiguration({ scope: nextScope })
                }
              }}
            >
              <SelectTrigger id={`template-${definition.key}-scope`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entire_web">
                  {t("workspace.agent.templates.webSearch.entireWeb")}
                </SelectItem>
                <SelectItem value="specific_websites">
                  {t("workspace.agent.templates.webSearch.specificWebsites")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {scope === "specific_websites" && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor={`template-${definition.key}-domain`}>
                {t("workspace.agent.templates.webSearch.allowedWebsites")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("workspace.agent.templates.webSearch.allowedWebsitesHelp", {
                  count: domainCount,
                  max: definition.maxDomains,
                })}
              </p>
            </div>
            {domainCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {derivedDomains.map((domain) => (
                  <Badge key={`derived-${domain}`} variant="secondary">
                    {domain}
                    <span className="ml-1 text-muted-foreground">
                      {t("workspace.agent.templates.webSearch.fromAnswers")}
                    </span>
                  </Badge>
                ))}
                {manualDomains.map((domain) => (
                  <Badge
                    key={`manual-${domain}`}
                    variant="secondary"
                    className="gap-1"
                  >
                    {domain}
                    <button
                      type="button"
                      aria-label={t(
                        "workspace.agent.templates.webSearch.removeWebsite",
                        {
                          domain,
                        }
                      )}
                      disabled={disabled}
                      onClick={() =>
                        updateConfiguration({
                          manualDomains: manualDomains.filter(
                            (candidate) => candidate !== domain
                          ),
                        })
                      }
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {definition.allowManualDomains && (
              <div className="flex gap-2">
                <Input
                  id={`template-${definition.key}-domain`}
                  value={domainInput}
                  disabled={disabled}
                  aria-invalid={domainInputError}
                  placeholder={t(
                    "workspace.agent.templates.webSearch.websitePlaceholder"
                  )}
                  onChange={(event) => {
                    setDomainInput(event.target.value)
                    setDomainInputError(false)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      addDomain()
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled || !domainInput.trim()}
                  onClick={addDomain}
                >
                  {t("workspace.agent.templates.webSearch.addWebsite")}
                </Button>
              </div>
            )}
            {domainInputError && (
              <p className="text-sm text-destructive">
                {t("workspace.agent.templates.webSearch.invalidWebsite")}
              </p>
            )}
          </div>
        )}

        {definition.allowSearchBoundary && (
          <div className="space-y-2">
            <Label htmlFor={`template-${definition.key}-boundary`}>
              {t("workspace.agent.templates.webSearch.boundary")}
            </Label>
            <Textarea
              id={`template-${definition.key}-boundary`}
              value={boundary}
              maxLength={definition.searchBoundaryMaxLength}
              disabled={disabled}
              placeholder={t(
                "workspace.agent.templates.webSearch.boundaryPlaceholder"
              )}
              onChange={(event) =>
                updateConfiguration({ boundary: event.target.value })
              }
            />
          </div>
        )}
      </div>
    </ConfigurableFieldShell>
  )
}
